import apiClient from "../services/apiClient"
import { setAccessToken } from "../services/apiClient"

export const handleLogout = async (): Promise<void> => {
  try {
    await apiClient.post("/auth/logout", undefined, { withCredentials: true })
  } catch {
    // proceed with local logout even if the server call fails
  }
  setAccessToken(null)
  window.location.href = "/login"
}

export const skipNull = (value: any): any => {
  if (value === null || value === undefined) return ""
  return value
}

export const skipNullWithDash = (value: any): any => {
  if (value === null || value === undefined) return "-"
  return value
}

/**
 * Drops keys whose value is an unset filter ("" / null / undefined).
 *
 * gs-backend-api validates list endpoints with Joi, and its optional filters are typed as
 * what they actually are: `userType` is a boolean, `userRole` is an enum of '0'-'5',
 * `dateFilter` is a non-empty string. Sending "" for an unset filter fails validation with
 * a 400, so "no filter applied" has to mean the key is absent, not present-but-empty.
 *
 * `search` is deliberately still safe to send empty (paginationBase allows ''), but it is
 * stripped here too, which the backend treats identically.
 */
export const omitEmpty = <T extends Record<string, any>>(params: T): Partial<T> => {
  const out: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === "" || value === null || value === undefined) continue
    out[key as keyof T] = value
  }
  return out
}

/**
 * Builds a Stripe Dashboard URL for the account the backend is actually talking to.
 *
 * Test-mode objects live under a `/test` path segment: a customer created with a
 * `sk_test_` key is at dashboard.stripe.com/test/customers/cus_x, and the same URL without
 * `/test` resolves against the live account, where that id does not exist. Linking without
 * the prefix therefore drops an admin on a "not found" page rather than the record they
 * asked for.
 *
 * The mode comes from the API (which reads it off the secret key in use), not from
 * anything the browser guesses.
 */
export const stripeDashboardUrl = (mode: "live" | "test" | undefined, path: string): string => {
  const clean = path.replace(/^\/+/, "")
  return mode === "live"
    ? `https://dashboard.stripe.com/${clean}`
    : `https://dashboard.stripe.com/test/${clean}`
}

/**
 * Formats a Stripe amount, which this app receives in cents (e.g. a subscription tier's
 * unitAmount). Use this for anything that came from the API, never for a dollar amount an
 * admin typed into a form - see formatDollars for that.
 */
export const formatCents = (cents?: number): string =>
  typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "-"

/**
 * Formats a dollar amount an admin entered directly into a form (one-time charges,
 * hardware sales). These forms collect whole dollars, not cents - see formatCents for a
 * Stripe API amount instead. Keeping the two separate is deliberate: the same-looking
 * $X.XX output hides a real unit difference, and pasting one helper into the other's
 * context would display an amount off by 100x.
 */
export const formatDollars = (amount: number | string): string => {
  const parsed = Number(amount)
  return `$${(Number.isFinite(parsed) ? parsed : 0).toFixed(2)}`
}
